import { ChevronDown } from 'lucide-react';
import { useId } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { signedKr } from '../../lib/money';
import { trpc } from '../../lib/trpc';
import { periodLabel } from '../bills/cost-history';
import { TableSkeleton } from '../common/Skeleton';
import { dayLabel } from '../purchases/dates';
import { SavingsChart, SavingsSparkline } from './SavingsChart';
import { averageNet, type SavingsPot } from './savings-history';

const RECENT_LIMIT = 20;

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-lg bg-muted/50 px-3 py-2">
      <div className="truncate text-xs text-muted-foreground">{label}</div>
      <div className="truncate text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function PotDetails({ pot }: { pot: SavingsPot }) {
  const txQuery = trpc.savings.transactions.useQuery(
    { categoryId: pot.categoryId, limit: RECENT_LIMIT },
    { retry: false },
  );
  const average = averageNet(pot.points);
  const rows = txQuery.data ?? [];

  return (
    <div className="space-y-4 border-t border-border px-4 pt-4 pb-4 sm:px-5">
      <SavingsChart
        points={pot.points}
        average={average}
        height={{ base: 170, sm: 210 }}
        ariaLabel={`${pot.name}, sparat netto per period`}
      />

      <div className="grid grid-cols-3 gap-2">
        <Stat label="Insatt" value={signedKr(pot.totalDeposited)} />
        <Stat
          label="Uttaget"
          value={pot.totalWithdrawn > 0 ? signedKr(-pot.totalWithdrawn) : '0 kr'}
        />
        <Stat label="Snitt" value={average != null ? signedKr(average) : '–'} />
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Senaste</h3>
        {txQuery.isLoading ? (
          <TableSkeleton rows={3} columns={2} />
        ) : txQuery.error ? (
          <p className="text-sm text-negative">Kunde inte hämta transaktionerna just nu.</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Inga transaktioner i den här potten än.</p>
        ) : (
          <ul className="divide-y divide-border rounded-xl border border-border">
            {rows.map((row) => {
              // A deposit leaves the everyday account (negative), so it reads as + saved
              const saved = -row.amount;
              return (
                <li key={row.id} className="flex items-start justify-between gap-3 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm">{row.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {dayLabel(row.date)}
                      {row.accountName ? ` · ${row.accountName}` : ''}
                      {` · ${periodLabel(row.period.year, row.period.month)}`}
                    </p>
                  </div>
                  <span
                    className={cn(
                      'shrink-0 text-sm font-medium whitespace-nowrap tabular-nums',
                      saved < 0 && 'text-negative',
                    )}
                  >
                    {saved < 0 ? `${signedKr(saved)} ut` : signedKr(saved)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {rows.length === RECENT_LIMIT && (
          <p className="text-xs text-muted-foreground">Visar de {RECENT_LIMIT} senaste.</p>
        )}
      </div>
    </div>
  );
}

/** One pot: net this period, total over the range and a sparkline; tap for details. */
export function SavingsPotCard({
  pot,
  range,
  expanded,
  onToggle,
}: {
  pot: SavingsPot;
  range: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const detailsId = useId();
  const current = pot.points.at(-1);
  const currentNet = current?.net ?? 0;

  return (
    <li className="overflow-hidden rounded-2xl border border-border bg-card">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={detailsId}
        className="block w-full px-4 pt-3.5 pb-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 sm:px-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-[15px] font-semibold">{pot.name}</span>
              {!pot.isActive && (
                <Badge variant="outline" className="text-muted-foreground">
                  Borttagen
                </Badge>
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground tabular-nums">
              Totalt {signedKr(pot.totalNet)} på {range} perioder
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <span className="text-right">
              <span
                className={cn(
                  'block text-base font-semibold tabular-nums',
                  currentNet < 0 && 'text-negative',
                )}
              >
                {signedKr(currentNet)}
              </span>
              <span className="block text-[11px] text-muted-foreground">denna period</span>
            </span>
            <ChevronDown
              aria-hidden
              className={cn(
                'size-4 text-muted-foreground transition-transform',
                expanded && 'rotate-180',
              )}
            />
          </div>
        </div>
        {/* The expanded chart below replaces the sparkline */}
        {!expanded && (
          <div className="mt-2.5">
            <SavingsSparkline points={pot.points} />
          </div>
        )}
      </button>

      <div id={detailsId} hidden={!expanded}>
        {expanded && <PotDetails pot={pot} />}
      </div>
    </li>
  );
}
